import React, { useState, useEffect } from 'react';
import { rotateMatrix } from './utils';

// Define BoardCell and Board types if not imported
export interface BoardCell {
  occupied: boolean;
  color?: string;
}
export type Board = BoardCell[][];

interface StuckBlock {
  row: number;
  col: number;
  color: string;
}

interface BlockGroupProps {
  blockSize: number;
  pattern: number[][];
  blockType: string; // Add blockType prop
  blockPatterns: { [key: string]: number[][][] }; // Add blockPatterns prop
  startDelay?: number;
  onReachBottom?: () => void;
  onBlockStuck?: (blocks: StuckBlock[], finalPattern: number[][], isTopRowStuck: boolean) => void;
  startingCol: number;
  board: Board;
  color: string;
  startingRotation: number;
  optimalRotation?: number; // Make optional since pattern is already optimally rotated
  onTopRowStuck?: () => void; // New optional callback
  onBlockCreated?: () => void; // Callback when block is created
}

const BlockGroup: React.FC<BlockGroupProps> = ({ 
  blockSize, 
  pattern, 
  blockType,
  blockPatterns,
  startDelay = 0, 
  onReachBottom, 
  onBlockStuck, 
  startingCol, 
  board,
  color,
  startingRotation = 0,
  optimalRotation = 0, // Default to 0 since pattern is already optimally rotated
  onTopRowStuck,
  onBlockCreated,
}) => {
  //const [rotationsLeft, setRotationsLeft] = useState(0); // No additional rotations needed
  const [currentRotation, setCurrentRotation] = useState(startingRotation);
  const [rotatedPattern, setRotatedPattern] = useState(pattern);
  // Remove duplicate startingRotation state
  const [isAnimating, setIsAnimating] = useState(true); // New state for initial animations
  const [currentCol, setCurrentCol] = useState(0);
  const [currentRow, setCurrentRow] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  
  // Function to reset state for a new block
  const resetBlockState = () => {
    setRotatedPattern(pattern);
    
    let initialLastNonEmptyRow = 0;
    for (let i = pattern.length - 1; i >= 0; i--) {
      if (pattern[i].some(cell => cell === 1)) {
        initialLastNonEmptyRow = i;
        break;
      }
    }
    setCurrentRow(-initialLastNonEmptyRow);
    
    // Reset other state for the new block
    const totalColumns = Math.floor(window.innerWidth / blockSize);
    setCurrentCol(Math.floor(totalColumns / 2) - 2);
    setIsAnimating(true);
    setIsStuck(false);
    setIsActive(startDelay === 0);
  };

  // Reset state when block is created
  useEffect(() => {
    resetBlockState();
    if (onBlockCreated) {
      onBlockCreated();
    }
  }, [pattern, optimalRotation, startDelay, blockSize]);

  // Find the last non-empty row in the pattern (from the bottom)
  const getLastNonEmptyRow = () => {
    for (let i = pattern.length - 1; i >= 0; i--) {
      if (pattern[i].some(cell => cell === 1)) {
        return i;
      }
    }
    return 0;
  };
  const lastNonEmptyRow = getLastNonEmptyRow();

  // Always start at the center column
  const totalColumns = Math.floor(window.innerWidth / blockSize);
  const centerCol = Math.floor(totalColumns / 2) - 2;
  // Restore currentRow for vertical movement
  // const [currentRow, setCurrentRow] = useState(-lastNonEmptyRow); // This line is now redundant
  // const [isActive, setIsActive] = useState(startDelay === 0); // This line is now redundant
  // const [isStuck, setIsStuck] = useState(false); // This line is now redundant
  
  // Calculate the center offset to align with red blocks
  const getCenterOffset = () => {
    const screenWidth = window.innerWidth;
    const groupWidth = blockSize * 4;
    return (screenWidth - groupWidth) / 2;
  };
  
  // Calculate the starting column for the block group based on currentCol (for rendering)
  const getRenderCol = () => currentCol;
  
  // For all collision and stuck logic, use the board
  const checkCollision = (row: number, col: number): boolean => {
    if (row >= 12) return true;
    if (col < 0 || col >= totalColumns) return true;
    return board[row]?.[col]?.occupied;
  };

  // Check if the entire pattern can move to the specified row
  const canMoveToRow = (row: number): boolean => {
    for (let patternRow = 0; patternRow < rotatedPattern.length; patternRow++) {
      for (let patternCol = 0; patternCol < rotatedPattern[patternRow].length; patternCol++) {
        if (rotatedPattern[patternRow][patternCol] === 1) {
          const worldRow = row + patternRow;
          // Use the currentCol (what the user actually sees) for horizontal position
          const worldCol = currentCol + patternCol;
          if (worldCol < 0 || worldCol >= totalColumns) {
            continue;
          }
          if (checkCollision(worldRow, worldCol)) {
            //console.log("canMoveToRow false for row:" + worldRow + " col: " + worldCol)
            return false;
          }
        }
      }
    }
    return true;
  };

  const canMoveToCol = (col: number): boolean => {
    for (let patternRow = 0; patternRow < rotatedPattern.length; patternRow++) {
      for (let patternCol = 0; patternCol < rotatedPattern[patternRow].length; patternCol++) {
        if (rotatedPattern[patternRow][patternCol] === 1) {
          const worldRow = currentRow + patternRow;
          const worldCol = col + patternCol;
          if (worldCol < 0 || worldCol >= totalColumns) {
            continue;
          }
          if (checkCollision(worldRow, worldCol)) {
            //console.log("canMoveToRow false for row:" + worldRow + " col: " + worldCol)
            return false;
          }
        }
      }
    }
    return true;
  };

  const canRotate = (rotation: number): boolean => {
    let tempPattern = rotateMatrix(blockPatterns[blockType], (rotation)%4);

    for (let patternRow = 0; patternRow < tempPattern.length; patternRow++) {
      for (let patternCol = 0; patternCol < tempPattern[patternRow].length; patternCol++) {
        if (tempPattern[patternRow][patternCol] === 1) {
          const worldRow = currentRow + patternRow;
          const worldCol = currentCol + patternCol;
          if (worldCol < 0 || worldCol >= totalColumns) {
            continue;
          }
          if (checkCollision(worldRow, worldCol)) {
            //console.log("canMoveToRow false for row:" + worldRow + " col: " + worldCol)
            return false;
          }
        }
      }
    }
    return true;
  };

  // Get stuck blocks from current pattern (for onBlockStuck)
  const getStuckBlocks = (row: number): StuckBlock[] => {
    const blocks: StuckBlock[] = [];
    for (let patternRow = 0; patternRow < rotatedPattern.length; patternRow++) {
      for (let patternCol = 0; patternCol < rotatedPattern[patternRow].length; patternCol++) {
        if (rotatedPattern[patternRow][patternCol] === 1) {
          const worldRow = row + patternRow;
          // Again, use currentCol so stuck blocks align with what was rendered
          const worldCol = currentCol + patternCol;
          blocks.push({
            row: worldRow,
            col: worldCol,
            color: board[worldRow]?.[worldCol]?.color || '#4444ff'
          });
        }
      }
    }
    return blocks;
  };

  // Check if the block group is fully visible on the board (vertical offset > 0)
  const isFullyVisible = (): boolean => {
    return currentRow >= 0;
  };

  // Combined animation and falling logic in same thread
  useEffect(() => {
    if ((!isAnimating && !isActive) || isStuck) return;

    let animationCounter = 0;
    let fallCounter = 0;
    const ANIMATION_INTERVAL = 100; // 100ms for fast animation
    const FALL_INTERVAL = 1000; // 1000ms for falling
    const TICK_INTERVAL = 100; // Check every 100ms

    const combinedTimer = setInterval(() => {
      animationCounter += TICK_INTERVAL;
      fallCounter += TICK_INTERVAL;

      // Handle fast animation (rotation and horizontal movement) - every 100ms
      if (isAnimating && animationCounter >= ANIMATION_INTERVAL) {
        if (isFullyVisible()) {
          if (currentRotation != optimalRotation) {
            if (canRotate(currentRotation + 1)) {
            // Rotate the pattern
              setRotatedPattern(rotateMatrix(blockPatterns[blockType], (currentRotation + 1)%4));
              setCurrentRotation(prev => (prev + 1)%4);
            } else {
              // Stop rotating if we can't rotate further without collision
              setIsAnimating(false);
            }
          } else if (currentCol !== startingCol) {
            // Determine the next horizontal step toward startingCol
            const nextCol = currentCol < startingCol ? currentCol + 1 : currentCol - 1;
            if (canMoveToCol(nextCol)) {
              // Move horizontally to optimal position
              setCurrentCol(nextCol);
            } else {
              // Stop moving horizontally if blocked
              setIsAnimating(false);
            }
          }
          // Check: is block stuck due to rotation or horizontal movement
          if (!canMoveToRow(currentRow + 1)) {
            if (onBlockStuck) {
              onBlockStuck(getStuckBlocks(currentRow), rotatedPattern, currentRow < 0 ? true : false);
            }
            // Mark as stuck and stop moving
            setIsStuck(true);
          }
        }
        animationCounter = 0; // Reset counter
      }

      // Handle falling logic - every 1000ms
      if (isActive && fallCounter >= FALL_INTERVAL) {
        setCurrentRow(prevRow => {
          // Check if we can move to the next row
          if (canMoveToRow(prevRow + 1)) {
            return prevRow + 1;
          } else {
            // We're stuck! Add blocks to stuck blocks
            if (onBlockStuck) {
              onBlockStuck(getStuckBlocks(prevRow), rotatedPattern, prevRow < 0 ? true : false);
            }
            // Mark as stuck and stop moving
            setIsStuck(true);
            
            // If we're at the bottom, notify parent
            if (prevRow >= 10 && onReachBottom) {
              onReachBottom();
            }
            
            return prevRow; // Stop moving
          }
        });
        fallCounter = 0; // Reset counter
      }
    }, TICK_INTERVAL);

    return () => clearInterval(combinedTimer);
  }, [currentCol, currentRow, currentRotation]);

  // Calculate horizontal position to align with red blocks
  const horizontalPosition = getCenterOffset();
  // Calculate the left offset of the grid (centered)
  const gridLeft = (window.innerWidth - totalColumns * blockSize) / 2;
  // Find the topmost and leftmost non-zero cell in the pattern
  let minPatternRow = pattern.length, minPatternCol = pattern[0].length;
  for (let r = 0; r < pattern.length; r++) {
    for (let c = 0; c < pattern[r].length; c++) {
      if (pattern[r][c] !== 0) {
        if (r < minPatternRow) minPatternRow = r;
        if (c < minPatternCol) minPatternCol = c;
      }
    }
  }
  // Calculate debug log position
  const debugLeft = gridLeft + (getRenderCol() + minPatternCol) * blockSize;
  const debugTop = (currentRow + minPatternRow) * blockSize - 30;

  return (
    <>
      {/* Position display text */}
      <div
        style={{
          position: 'fixed',
          top: `${Math.max(0, debugTop)}px`,
          left: `${debugLeft}px`,
          color: 'white',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '5px 10px',
          borderRadius: '5px',
          fontSize: '14px',
          fontFamily: 'monospace',
          zIndex: 4,
          opacity: isActive ? 1 : 0
        }}
      >
        Col: {getRenderCol()}, Row: {currentRow}
      </div>
      
      {/* Block group */}
      <div 
        className="block-group"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.floor(window.innerWidth / blockSize)}, ${blockSize}px)`,
          gridTemplateRows: `repeat(12, ${blockSize}px)`,
          zIndex: 3,
          width: `${Math.floor(window.innerWidth / blockSize) * blockSize}px`,
          height: `${12 * blockSize}px`,
          transition: isStuck ? 'none' : 'top 0.1s ease-out',
          opacity: isActive ? 1 : 0
        }}
      >
        {/* Render empty grid cells */}
        {Array.from({ length: 12 * Math.floor(window.innerWidth / blockSize) }, (_, index) => {
          const row = Math.floor(index / Math.floor(window.innerWidth / blockSize));
          const col = index % Math.floor(window.innerWidth / blockSize);
          
          // Check if this position should have a blue block
          const patternRow = row - currentRow;
          const patternCol = col - getRenderCol();
          const hasBlock = patternRow >= 0 && patternRow < rotatedPattern.length && 
                          patternCol >= 0 && patternCol < rotatedPattern[patternRow].length && 
                          rotatedPattern[patternRow][patternCol] === 1;
          
          return (
            <div
              key={`grid-${index}`}
              style={{
                width: `${blockSize}px`,
                height: `${blockSize}px`,
                backgroundColor: hasBlock ? '#4444ff' : 'transparent',
                border: hasBlock ? '5px solid #3333cc' : 'none',
                boxSizing: 'border-box'
              }}
            />
          );
        })}
      </div>
    </>
  );
};

export default BlockGroup;